import { useCallback, useEffect, useRef, useState } from 'react';
import type { ResultsNoticeView } from '../../shared/results_page/results_notice.js';
import { useStableCallback } from '../../shared/use_stable_callback.js';

/**
 * As páginas de ofertas de carro de UMA busca, para a `RentalResultsPage`: a
 * bancada do web-chat (página 1 já veio da busca fresca) e o takeover da
 * conversa (nasce com o preview do card e pede a página 1 ao montar). As
 * páginas seguintes só ACRESCENTAM, com dedupe por `offerId` (o preview e a
 * página 1 se sobrepõem); o corte do cliente roda sobre tudo o que já veio.
 *
 * Parte do corte vai ao servidor (`rentalServerFiltersOf`, #5121): quando a
 * `queryKey` muda, as páginas acumuladas são de outra consulta e a paginação
 * recomeça na 1ª. As linhas antigas ficam em cena até ela chegar — o corte do
 * cliente já as filtra — e são trocadas por ela inteiras.
 *
 * A busca é do host (`fetchPage`): o kit não faz IO. `reqRef` descarta
 * respostas obsoletas — a de uma página pedida antes de um "tentar de novo" ou
 * de uma troca de filtro. A falha guarda a PÁGINA pedida: o "tentar de novo"
 * repete essa, não a 1ª, que apagaria as linhas já acumuladas. Uma busca nova
 * é um hook novo: o host remonta por chave.
 *
 * Cada oferta lembra a PÁGINA que a trouxe (`pageOf`): com a rolagem infinita,
 * a ordem da página só reordena dentro de cada página e nunca mexe no que já
 * está em cena (#4513, ver `sortRentalOffers`). O servidor já pagina numa ordem
 * só (dentro da política, depois preço).
 */

export type RentalOfferPage = { number: number; hasMore: boolean };

export type RentalOfferPageResult<T> = { offers: readonly T[]; page: RentalOfferPage };

export type UseRentalOfferPagesOptions<T> = {
  /** Ausente (host sem busca), a lista é a inicial e nada é pedido. */
  fetchPage?: (page: number) => Promise<RentalOfferPageResult<T>>;
  /** A identidade dos filtros que o `fetchPage` manda ao servidor. Mudou, a
   *  paginação recomeça na página 1. Ausente = uma consulta só. */
  queryKey?: string;
  /** A lista em cena ao montar. Com `page`, ela JÁ é a página 1; sem, é um
   *  preview e o hook pede a página 1. */
  initial: { offers: readonly T[]; page?: RentalOfferPage };
};

export type RentalOfferPages<T> = {
  offers: readonly T[];
  /** A 1ª página ainda não chegou: a lista em cena é o preview. */
  settling: boolean;
  loading: boolean;
  failed: boolean;
  hasMore: boolean;
  loadMore: () => void;
  retry: () => void;
  /** A página que trouxe a oferta (1 para a lista inicial). */
  pageOf: (offerId: string) => number;
};

const UNLOADED: RentalOfferPage = { number: 0, hasMore: true };

export function useRentalOfferPages<T extends { offerId: string }>({
  fetchPage,
  queryKey,
  initial,
}: UseRentalOfferPagesOptions<T>): RentalOfferPages<T> {
  const [rows, setRows] = useState<readonly T[]>(initial.offers);
  const [pageIndex, setPageIndex] = useState<ReadonlyMap<string, number>>(
    () => new Map(initial.offers.map((offer) => [offer.offerId, 1])),
  );
  const [page, setPage] = useState<RentalOfferPage>(initial.page ?? UNLOADED);
  const [loading, setLoading] = useState(false);
  const [failedPage, setFailedPage] = useState<number | null>(null);
  const reqRef = useRef(0);
  const canFetch = fetchPage !== undefined;

  // A busca mais recente sem reabrir o efeito da página 1 a cada render do host.
  const load = useStableCallback(async (pageNumber: number) => {
    const request = fetchPage;
    if (!request) return;
    const reqId = ++reqRef.current;
    setLoading(true);
    setFailedPage(null);
    try {
      const res = await request(pageNumber);
      if (reqId !== reqRef.current) return;
      setRows((prev) => {
        if (pageNumber <= 1) return res.offers;
        const seen = new Set(prev.map((offer) => offer.offerId));
        return [...prev, ...res.offers.filter((offer) => !seen.has(offer.offerId))];
      });
      setPageIndex((prev) => {
        // A primeira página que trouxe a oferta é a dela: o dedupe acima
        // descarta a repetição, então a página não muda depois.
        const next = new Map(pageNumber <= 1 ? [] : prev);
        res.offers.forEach((offer) => {
          if (!next.has(offer.offerId)) next.set(offer.offerId, Math.max(1, pageNumber));
        });
        return next;
      });
      setPage(res.page);
    } catch {
      if (reqId === reqRef.current) setFailedPage(pageNumber);
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  });

  const preview = initial.page === undefined;
  useEffect(() => {
    if (preview) void load(1);
  }, [preview, load]);

  // A consulta em cena: a da montagem já está nas linhas iniciais (ou no
  // pedido do preview acima), então só uma TROCA recomeça.
  const queryRef = useRef(queryKey);
  // A consulta que as linhas em cena respondem. Enquanto ela difere da pedida,
  // a lista já está em carga — desde o render da troca, antes do efeito pedir
  // a página: sem isso o host via, por um quadro, o corte novo sobre as linhas
  // velhas fora de carga e dizia "nenhum carro" (#6268).
  const [loadedQuery, setLoadedQuery] = useState(queryKey);
  useEffect(() => {
    if (queryRef.current === queryKey) return;
    queryRef.current = queryKey;
    setLoadedQuery(queryKey);
    // Sem página carregada, a rolagem não pede a 2ª da consulta velha.
    setPage(UNLOADED);
    void load(1);
  }, [queryKey, load]);

  // Estável entre renders: os hosts a põem nas dependências da projeção.
  const pageOf = useCallback((offerId: string) => pageIndex.get(offerId) ?? 1, [pageIndex]);
  const failed = failedPage !== null;
  return {
    offers: rows,
    settling: canFetch && page.number === 0 && !failed,
    loading: loading || loadedQuery !== queryKey,
    failed,
    hasMore: page.number > 0 && page.hasMore,
    loadMore: () => {
      if (!loading && page.hasMore) void load(Math.max(1, page.number) + 1);
    },
    retry: () => void load(failedPage ?? 1),
    pageOf,
  };
}

export type RentalOfferPagesNoticeCopy = {
  /** A linha da 1ª página a caminho. Ausente (host que já nasce com a página
   *  1), não há linha. */
  loadingLabel?: string;
  errorLabel: string;
  retryLabel: string;
};

/** O aviso da página: a falha vira cartão com "tentar de novo"; a 1ª página a
 *  caminho vira a linha discreta ao lado da contagem. */
export function rentalOfferPagesNotice(
  pages: Pick<RentalOfferPages<unknown>, 'failed' | 'settling' | 'retry'>,
  copy: RentalOfferPagesNoticeCopy,
): ResultsNoticeView | undefined {
  if (pages.failed) {
    return {
      tone: 'error',
      title: copy.errorLabel,
      actionLabel: copy.retryLabel,
      onAction: pages.retry,
    };
  }
  return pages.settling && copy.loadingLabel
    ? { tone: 'loading', title: copy.loadingLabel }
    : undefined;
}
